import React from "react";

/* How far through signup you are, in words as well as dots.

   The talent flow already drew a row of dots, but they were aria-hidden with no
   label, so they showed position without ever answering the question people
   actually have on a signup form: how much more of this is there. The company
   flow had nothing at all -- you pressed a button and got a form with no idea
   whether it was the first of two screens or the first of six.

   Says the remaining count out loud rather than leaving it to be counted off
   filled dots. "Step 2 of 3" is the position; "1 left" is the promise, and the
   promise is the part that keeps somebody going.

   `total` of 1 renders nothing. A lone dot labelled "last step" is noise on a
   screen that was never a sequence -- which is what the company path looks like
   when somebody picks "just create my account" and skips the composer. */
export default function StepProgress({ step, total, className = "" }) {
  if (!total || total < 2) return null;

  const current = Math.min(Math.max(step, 1), total);
  const remaining = total - current;

  return (
    <div className={`mb-6 ${className}`}>
      <div className="flex items-center gap-1.5" aria-hidden="true">
        {Array.from({ length: total }, (_, i) => (
          <span
            key={i}
            className="h-1.5 rounded-full transition-all"
            style={{
              width: i === current - 1 ? 28 : 14,
              background: i <= current - 1 ? "var(--brand)" : "var(--border-strong, #ddd)",
            }}
          />
        ))}
      </div>
      {/* One live region for screen readers, and the same words on screen. */}
      <p className="mt-2 text-xs text-secondary" aria-live="polite">
        {/* "Step 1 of 3" already says how much is left; "· 2 more steps" said
            it twice (Jake, 2026-10-04). */}
        {remaining === 0 ? (
          <span className="font-semibold text-brand-ink">Last step</span>
        ) : (
          <>Step {current} of {total}</>
        )}
      </p>
    </div>
  );
}
